import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import { CustomTheme } from "../../../../types/customTheme";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import {
  approveChangeRequest,
} from "../../../../services/locationApp/licenseService";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import Cookies from "js-cookie";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 10,
    },
    pill: {
      borderRadius: 8,
      padding: "2px 8px",
      fontSize: "12px",
      textAlign: "center",
      fontWeight: 600,
      minWidth: 70,
      display: "inline-block",
      width: "fit-content",
    },
    detailItem: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      marginBottom: theme.spacing(1),
      fontSize: "14px",
      gap: theme.spacing(1),
    },
    detailLabel: {
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
  }),
);

interface Props {
  open: boolean;
  onClose: () => void;
  request: any;
  onSuccess: () => void;
}

const formatKey = (key: string) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

const ChangeRequestDetailsModal: React.FC<Props> = ({
  open,
  onClose,
  request,
  onSuccess,
}) => {
  const classes = useStyles();
  const theme = useTheme<CustomTheme>();
  const [success, setSuccess] = useState("");

  const isRequestPending = request?.status?.toLowerCase() === "requested";

  // ✅ Get token from cookies
  const getToken = (): string | null => {
    return Cookies.get("idToken") || null;
  };

  const getStatusStyles = (status: string) => {
    const map: Record<string, { backgroundColor: string; color: string }> = {
      approved: {
        backgroundColor: theme.palette.custom.green.main || "#4CAF50",
        color: theme.palette.custom.green.contrastText || "#fff",
      },
      trial: {
        backgroundColor: theme.palette.custom.blue.main || "#2196f3",
        color: theme.palette.custom.blue.contrastText || "#fff",
      },
      denied: {
        backgroundColor: theme.palette.custom.red.main || "#f44336",
        color: theme.palette.custom.red.contrastText || "#fff",
      },
      changing: {
        backgroundColor: theme.palette.custom.yellow.main || "#FFEB3B",
        color: theme.palette.custom.yellow.contrastText || "#000",
      },
      requested: {
        backgroundColor: theme.palette.custom.yellow.main || "#FFEB3B",
        color: theme.palette.custom.yellow.contrastText || "#000",
      },
    };
    return map[status.toLowerCase()] || { backgroundColor: "#ccc", color: "#000" };
  };

  return (
    <>
      <DefaultAlert
        open={!!success}
        handleClose={() => setSuccess("")}
        message={success}
        severity="success"
      />

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>Change Request Details</DialogTitle>
        <DialogContent>
          {request ? (
            <Box display="flex" flexDirection="column">
              {Object.entries(request).map(([key, value]) => (
                <Box key={key} className={classes.detailItem}>
                  <Typography className={classes.detailLabel}>
                    {formatKey(key)}:
                  </Typography>
                  {key === "status" ? (
                    <span
                      className={classes.pill}
                      style={getStatusStyles(value as string)}
                    >
                      {(value as string).charAt(0).toUpperCase() +
                        (value as string).slice(1)}
                    </span>
                  ) : (
                    <Typography>{value as string}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography>No request data available</Typography>
          )}
        </DialogContent>

        <DialogActions style={{ width: "100%", padding: "16px 24px" }}>
          <Box display="flex" justifyContent="space-between" width="100%">
            <ButtonCommon
              variant="contained"
              color="orange"
              onClick={onClose}
              style={{ minWidth: 120, height: 36 }}
            >
              Cancel
            </ButtonCommon>

            <Box display="flex" style={{ gap: 8 }}>
              <ButtonCommon
                variant="contained"
                color="red"
                disabled={!isRequestPending}
                onClick={async () => {
                  if (!request) return;
                  const token = getToken();
                  if (!token) return;

                  try {
                    await approveChangeRequest(
                      request.locationId,
                      {
                        changeRequestId: request.changeRequestId,
                        status: "denied",
                      },
                      token
                    );
                    setSuccess("Change request denied.");
                    onSuccess();
                    onClose();
                  } catch (err) {
                    console.error("❌ Deny failed:", err);
                  }
                }}
                style={{ minWidth: 120, height: 36 }}
              >
                Deny
              </ButtonCommon>

              <ButtonCommon
                variant="contained"
                color="green"
                disabled={!isRequestPending}
                onClick={async () => {
                  if (!request) return;
                  const token = getToken();
                  if (!token) return;

                  try {
                    await approveChangeRequest(
                      request.locationId,
                      {
                        changeRequestId: request.changeRequestId,
                        status: "approved",
                      },
                      token
                    );
                    setSuccess("Change request approved successfully.");
                    onSuccess();
                    onClose();
                  } catch (err) {
                    console.error("❌ Approve request failed:", err);
                  }
                }}
                style={{ minWidth: 120, height: 36 }}
              >
                Approve
              </ButtonCommon>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangeRequestDetailsModal;
